import { ROOT_FOLDER_ID, FolderAssetType } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { getRepository } from './image-repository';
import { getFolderRepository } from './folder-repository';
import { getBackgroundChangeRepository } from './background-change-repository';
import { transformImageUrlToImageDataAsync } from './request-transformers';

export const getEditImageRepository = ({
  imageODataService,
  imageBulkEditODataService,
  backgroundChangeODataService,
  folderODataService,
  i18n,
  ianaTimeZone,
  timezoneUTCOffset,
}) => {
  const repository = getRepository({
    imageODataService,
    imageBulkEditODataService,
    i18n,
    ianaTimeZone,
    timezoneUTCOffset,
  });

  const folderRepository = getFolderRepository({
    folderODataService,
    i18n,
    ianaTimeZone,
    timezoneUTCOffset,
  });

  const backgroundChangeRepository = getBackgroundChangeRepository(backgroundChangeODataService);

  const cropImage = ({ imageId, cropSettings, placementSettings }) => {
    const promise = repository.cropImage({ imageId, cropSettings, placementSettings });
    return promise;
  };

  const generateImageWithSmartEffect = ({ imageUrl, smartEffects }) => {
    const promise = repository.generateImageWithSmartEffect({ imageUrl, smartEffects });
    return promise;
  };

  const saveImageCopy = ({ imageUrl, folderId, sourceType }) => {
    const promise = folderRepository.addUrlImageToFolder({
      imageSrc: imageUrl,
      folderId,
      sourceType,
    });
    return promise;
  };

  const saveImage = ({ imageUrl, imageId, type }) => {
    const promise = transformImageUrlToImageDataAsync(imageUrl)
      .then(imageData => repository.updateImageData({
        imageId,
        imageData,
        type,
      }));
    return promise;
  };

  const getImageById = ({ imageId, isImageFittingEnabled }) => {
    const promise = repository.getImage(imageId, isImageFittingEnabled);
    return promise;
  };

  const uploadImageToFolder = ({
    imageData,
    folderId,
  }) => {
    const promise = folderRepository.addFolderUploadAsset({
      folderId,
      asset: { data: imageData },
      folderAssetType: FolderAssetType.Image,
    });
    return promise;
  };

  const saveImageWithBaseId = ({ image, baseId, folderId }) => {
    const promise = repository.saveImageWithBaseId({ image, baseId, folderId });
    return promise;
  };

  const removeImageBackground = (imageUrl) => {
    const promise = backgroundChangeRepository.removeImageBackground(imageUrl);
    return promise;
  };

  const getImageSegmentations = (imageUrl) => {
    const promise = backgroundChangeRepository.getImageSegmentations(imageUrl);
    return promise;
  };

  const generateImageBackground = ({
    prompt,
    // it could be original image url, in such case, the selectedMasks contains the mask ids user selected
    // if it's the url returned from 'removeImageBackground', the selectedMasks should be null
    imageUrl,
    // array of mask ids, if it's empty [], it means no mask is selected
    // if it's null, it means no masks are applied
    maskSetsBase64,
    selectedMasks,
    outputImageWidth,
    outputImageHeight,
  }) => {
    const promise = backgroundChangeRepository.generateImageBackground({
      prompt,
      imageUrl,
      maskSetsBase64,
      selectedMasks,
      outputImageWidth,
      outputImageHeight,
    });
    return promise;
  };

  const uploadStockImageToFolder = ({
    stockImageId,
    folderId = ROOT_FOLDER_ID,
  }) => {
    const promise = folderRepository.addStockImageToFolder({
      folderId,
      stockImageId,
    });
    return promise;
  };

  const moveImageToFolder = ({ image, destFolderId }) => {
    const promise = folderRepository.batchMove({
      assets: [image],
      destFolderId,
    });
    return promise;
  };

  const imageFit = ({ imageUrl, placements, skipOutpainting }) => {
    const promise = repository.imageFit({
      imageUrl,
      placements,
      skipOutpainting,
    });
    return promise;
  };

  const addImageWithBaseIdToFolder = ({
    imageSrc, baseId, folderId, metaData,
  }) => {
    const promise = folderRepository.addImageWithBaseIdToFolder({
      imageSrc,
      baseId,
      folderId,
      metaData,
    });
    return promise;
  };

  return {
    cropImage,
    generateImageWithSmartEffect,
    saveImage,
    saveImageCopy,
    getImageById,
    uploadImageToFolder,
    saveImageWithBaseId,
    removeImageBackground,
    getImageSegmentations,
    generateImageBackground,
    uploadStockImageToFolder,
    moveImageToFolder,
    imageFit,
    addImageWithBaseIdToFolder,
  };
};
